import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, Loader2, Lock, MessagesSquare, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, EmptyState } from "../components/ui";
import Avatar from "../components/Avatar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

/**
 * Conversations, from both sides.
 *
 * One page rather than two, because the two roles are the same object seen from
 * different ends: a member waiting on an answer and an investor deciding whether to
 * give one are looking at the same grant. `role` on each row says which end this
 * viewer is at, and the server decides that — not the client.
 *
 * The composer is disabled from `can_write`, which the server computes from the
 * grant's live status. A closed window locks both people out; that is one rule, and
 * this page does not re-implement it.
 */
function StatusPill({ grant, t }) {
  const map = {
    pending: { icon: Clock, cls: "bg-amber-50 text-amber-700" },
    active: { icon: Check, cls: "bg-emerald-50 text-emerald-600" },
    declined: { icon: X, cls: "bg-slate-100 text-slate-500" },
    lapsed: { icon: Clock, cls: "bg-slate-100 text-slate-500" },
    closed: { icon: Lock, cls: "bg-slate-100 text-slate-500" },
    reset: { icon: Lock, cls: "bg-slate-100 text-slate-500" },
  };
  const { icon: Icon, cls } = map[grant.status] || map.closed;
  return (
    <span className={`pill ${cls} flex items-center gap-1 shrink-0`}>
      <Icon size={11} /> {t(`dm.status.${grant.status}`)}
    </span>
  );
}

export default function Messages() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.dmConversations();
      setRows(data.conversations || []);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openThread = useCallback(async (id) => {
    setOpenId(id); setThread(null);
    try {
      const { data } = await api.dmThread(id);
      setThread(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  }, [t]);

  useEffect(() => {
    if (thread) endRef.current?.scrollIntoView({ block: "end" });
  }, [thread]);

  const respond = async (id, accept) => {
    setBusy(true);
    try {
      await api.dmRespond(id, accept);
      toast.success(accept ? t("dm.accepted") : t("dm.declined"));
      await load();
      await openThread(id);
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.dmSend(openId, body);
      setDraft("");
      await openThread(openId);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const grant = thread?.grant;

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-24 md:pb-6">
      <div>
        <div className="flex items-center gap-2 text-brand-600">
          <MessagesSquare size={17} />
          <span className="text-xs font-semibold uppercase tracking-[0.12em]">{t("dm.eyebrow")}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">
          {t("dm.title")}
        </h1>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed max-w-2xl">{t("dm.subtitle")}</p>
      </div>

      {rows === null ? (
        <Skeleton className="h-48" />
      ) : rows.length === 0 ? (
        <EmptyState
          testid="dm-empty"
          title={t("dm.emptyTitle")}
          description={t("dm.emptyBody")}
          action={<Link to="/investors" className="btn-primary">{t("dm.browseInvestors")}</Link>}
        />
      ) : (
        <div className="grid md:grid-cols-[minmax(0,20rem)_1fr] gap-4">
          <Panel className="card p-2" testid="dm-list">
            {rows.map((r) => (
              <button
                key={r.id}
                data-testid={`dm-row-${r.id}`}
                onClick={() => openThread(r.id)}
                className={`w-full text-left px-3 py-3 rounded-xl flex items-start gap-3 transition-colors ${
                  openId === r.id ? "bg-brand-50" : "hover:bg-slate-50"}`}
              >
                <Avatar name={r.counterparty_name} size={34} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{r.counterparty_name}</span>
                    {r.unread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-brand-500 text-white text-[10px] flex items-center justify-center shrink-0">
                        {r.unread}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusPill grant={r} t={t} />
                    <span className="text-[11px] text-slate-400">
                      {shortDate(r.last_message_at || r.requested_at)}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </Panel>

          <Panel className="card p-0 flex flex-col min-h-[26rem]" testid="dm-thread">
            {!openId ? (
              <div className="flex-1 grid place-items-center text-sm text-slate-400 p-8">
                {t("dm.pickOne")}
              </div>
            ) : !thread ? (
              <div className="flex-1 grid place-items-center p-8">
                <Loader2 className="animate-spin text-brand-500" size={22} />
              </div>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                  <Avatar name={grant.counterparty_name} size={34} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{grant.counterparty_name}</div>
                    <div className="text-[11px] text-slate-400">
                      {grant.status === "active" && grant.expires_at
                        ? t("dm.closesOn", { date: shortDate(grant.expires_at) })
                        : grant.status === "pending"
                          ? t("dm.awaiting", { date: shortDate(grant.request_expires_at) })
                          : t(`dm.status.${grant.status}`)}
                    </div>
                  </div>
                  <StatusPill grant={grant} t={t} />
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                        m.mine ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                        {m.body}
                        <div className={`mt-1 text-[10px] ${m.mine ? "text-white/70" : "text-slate-400"}`}>
                          {shortDate(m.at)}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>

                {/* The investor's decision. Shown only to the side that has one to make. */}
                {grant.status === "pending" && grant.role === "investor" && (
                  <div className="border-t border-slate-100 p-4 space-y-3" data-testid="dm-decide">
                    <p className="text-sm text-slate-600 leading-relaxed">{t("dm.decideBody")}</p>
                    <div className="flex gap-2">
                      <button className="btn-primary flex-1 justify-center" data-testid="dm-accept"
                              disabled={busy} onClick={() => respond(grant.id, true)}>
                        <Check size={16} /> {t("dm.accept")}
                      </button>
                      <button className="btn-outline flex-1 justify-center" data-testid="dm-decline"
                              disabled={busy} onClick={() => respond(grant.id, false)}>
                        <X size={16} /> {t("dm.decline")}
                      </button>
                    </div>
                  </div>
                )}

                {grant.can_write ? (
                  <div className="border-t border-slate-100 p-3 flex items-end gap-2">
                    <textarea
                      data-testid="dm-composer"
                      rows={2}
                      className="input flex-1 resize-none"
                      maxLength={4000}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={t("dm.composerPlaceholder")}
                    />
                    <button className="btn-primary" data-testid="dm-send-message"
                            onClick={send} disabled={busy || !draft.trim()}>
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                  </div>
                ) : grant.status !== "pending" && (
                  <div className="border-t border-slate-100 p-4 text-sm text-slate-500 flex items-center gap-2"
                       data-testid="dm-locked">
                    <Lock size={14} className="shrink-0" />
                    {grant.role === "member" ? t("dm.lockedMember") : t("dm.lockedInvestor")}
                  </div>
                )}
              </>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
